import designServices, { type RetrieveSystemDesignResponse } from '../../../../services/oneButtonServices/designServices';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import {
    annualProductionKwhFromViewerActual,
    applyFinalizedViewerMetricsToSystemDesign,
} from '../../../../helpers/systemDesignSolarMetrics';
import { COMMON_CONSTANTS, useOneButtonStore } from '../../../../store/oneButtonStore';
import { useViewerStore } from '../../../../store/store';
import { opportunityHasSolarFinancePartner } from './ProductionsCard';
import { UPDATE_LOAN_STATUS_SYSTEM_DESIGN_FIELD } from './BatteryDetailsModal';

const { SolarProductName, RoofProductName, BatteryProductName, RRRProductName } = COMMON_CONSTANTS;

function mapRetrieveToSavePayload(response: RetrieveSystemDesignResponse): Record<string, unknown> {
    const activeSolar = (response?.OpportunityProductsFinanceInfo || []).find(
        (item) => item?.ActiveFlag && item?.Product_Type_Name === 'Solar',
    );

    return {
        Opportunity_Id: response?.Opportunity_Id,
        OpportunityInterest: response?.OpportunityInterest,
        SalesForce_Id: response?.SalesForce_Id,
        SolarProductId: response?.SolarProductId,
        SolarUtility: response?.SolarUtility,
        BatteryInformation: response?.BatteryInformation,
        BatteryProductId: response?.BatteryProductId,
        Finance_Partner_Sync_CO_LeadID: response?.Finance_Partner_Sync_CO_LeadID,
        BatteryOnly: response?.BatteryOnly,
        BatteryOnlyProductId: response?.BatteryOnlyProductId,
        RoofOnly: response?.RoofOnly,
        RoofProductId: response?.RoofProductId,
        Roof: response?.Roof,
        RoofOnlyProductId: response?.RoofOnlyProductId,
        Multiplier: response?.Multiplier,
        SystemSize: response?.SystemSize,
        SystemSizeDouble: response?.SystemSizeDouble,
        Production: response?.Production,
        ProductionDouble: response?.ProductionDouble,
        IsDomestic: response?.IsDomestic ?? false,
        FinanceMethod: activeSolar?.Finance_Method_Name || response?.FinanceMethod || '',
        designJson: JSON.stringify(response?.DesignArray || []),
        isManual: response?.isManual ?? false,
    };
}

function getNormalizationCode(type: string): string {
    const t = (type || '').toLowerCase();
    if (t.includes(SolarProductName.toLowerCase())) return 'S';
    if (t.includes(RoofProductName.toLowerCase())) return 'R';
    if (t.includes(BatteryProductName.toLowerCase())) return 'B';
    if (t.includes(RRRProductName.toLowerCase())) return 'RRR';
    return 'S';
}

type RefreshSystemDesignAfterFinalizeArgs = {
    opportunityId: string;
};

/**
 * Pull the latest oneDRAW system design into oneBUTTON after finalization.
 * Does not depend on ProductionsCard / the refresh button being mounted in the DOM.
 */
export async function runRefreshSystemDesignAfterFinalizeAction(
    args: RefreshSystemDesignAfterFinalizeArgs,
): Promise<void> {
    const { opportunityId } = args;
    const id = String(opportunityId || '').trim();
    if (!id) return;

    const store = useOneButtonStore.getState();
    store.setSystemDesignRefreshing(true);

    try {
        let retrievedDesignRaw: RetrieveSystemDesignResponse | null = null;
        const res = await designServices.checkNewDesignExist(id)
        if(res.data.Success && id){
            retrievedDesignRaw = await designServices.RetrieveSystemDesign(id ?? '');
        } 
        const viewerState = useViewerStore.getState();
        const finalizedProductionKwh = annualProductionKwhFromViewerActual(viewerState.actualWattage);
        const retrievedDesign = retrievedDesignRaw
            ? (applyFinalizedViewerMetricsToSystemDesign(retrievedDesignRaw, {
                  actualKwh: finalizedProductionKwh,
                  systemKw: viewerState.totalSystemSize,
                  multiplier: viewerState.multiplier,
              }) as RetrieveSystemDesignResponse)
            : null;
        const [losses, designImage, designHistory] = await Promise.all([
            designServices.SystemDesignLosses(id),
            designServices.GetDesignImage(id),
            designServices.GetDesignHistory(id),
        ]);

        const {
            setSystemDesign,
            setSystemDesignLosses,
            setDesignImage,
            setDesignHistory,
            setPriceQuoteDetails,
            setQuotesRefreshTrigger,
            setPricingNetworkRefreshTrigger,
            setSystemDesignRefreshTrigger,
            opportunityDetails,
            productType,
        } = useOneButtonStore.getState();

        setSystemDesignLosses(losses ?? null);
        setDesignImage(designImage ?? null);
        setDesignHistory(designHistory ?? null);

        if (!retrievedDesign) return;

        setSystemDesign(retrievedDesign);

        try {
            const saveRes = await designServices.SaveSystemDesign(mapRetrieveToSavePayload(retrievedDesign));
            if (saveRes) {
                const priceQuote = await quoteServices.getRetrievePriceQuote(id);
                if (priceQuote) setPriceQuoteDetails(priceQuote as any);

                if (opportunityHasSolarFinancePartner(opportunityDetails, COMMON_CONSTANTS.GoodLeapPartnerName)) {
                    await quoteServices.contractUpdateLoanStatus(id, UPDATE_LOAN_STATUS_SYSTEM_DESIGN_FIELD, 0);
                    await quoteServices.contractGetLoanStatusByOpportunity(id);
                }

                if (
                    opportunityHasSolarFinancePartner(opportunityDetails, COMMON_CONSTANTS.LightreachPartnerName) ||
                    opportunityHasSolarFinancePartner(opportunityDetails, COMMON_CONSTANTS.IGSPartnerName)
                ) {
                    const productCode = getNormalizationCode('Solar');
                    await quoteServices.createLeadSystem({
                        productSelection: {
                            contractType: '',
                            Product_Category: productType || productCode,
                            LeadForProduct: productCode,
                        },
                        opportunityId: id,
                    });
                }
            }
        } catch (saveError) {
            try {
                const priceQuote = await quoteServices.getRetrievePriceQuote(id);
                if (priceQuote) setPriceQuoteDetails(priceQuote as any);
            } catch {
                // ignore quote fallback errors
            }
            console.error('Save System Design after finalize refresh failed:', saveError);
        }

        const now = Date.now();
        setQuotesRefreshTrigger(now);
        setPricingNetworkRefreshTrigger(now);
        setSystemDesignRefreshTrigger(now);
    } catch (error) {
        console.error('System design refresh after finalize failed:', error);
    } finally {
        useOneButtonStore.getState().setSystemDesignRefreshing(false);
    }
}
